/**
 * Canonical unit conversion — the full conversion authority.
 *
 * The npm `convert` engine (unitConversion.ts) only knows the units it ships:
 * it has no BTU / therm, no CO2e, and no fuel energy-content. This module adds
 * those as *derived* units — a linear factor onto a native `convert` anchor
 * (kWh, m³, ft³, kg) — keyed by descriptive canonical names ("KilowattHours",
 * "SquareFeet", "KBtus"). It also folds in the resource-typed fuel energy layer
 * (fuel.ts), so a fuel quantity converts to energy when a `ResourceType` is
 * supplied.
 *
 * Canonical names are the canonical unit-of-measure names and are the
 * vocabulary of the source BEDES data; the bedes layer normalizes messy spellings onto
 * these before calling in. Any consumer (carbon, materials, BEDES) can convert
 * BTU/therm/CO2e/fuel through here — plain `convert`-known units still go
 * through `convertValue` in unitConversion.ts.
 */
import { canConvert, convertValue } from "./unitConversion.js";
import { FUEL_ENERGY_KWH, remapFuelUnit } from "./fuel.js";
const nativeUnit = (code, dimension) => ({
    kind: "native",
    code,
    dimension,
});
const derivedUnit = (base, factor, dimension) => ({
    kind: "derived",
    base,
    factor,
    dimension,
});
/** 1 kBtu in kWh — the anchor factor for the Btu/therm family. */
const KWH_PER_KBTU = 0.29307107;
/**
 * Canonical unit name -> `convert`-anchored unit. Fuel energy-content units
 * (fuel.ts) are merged in below.
 */
export const UNIT_TABLE = {
    // Energy — watt-hour / joule families are native to `convert`
    KilowattHours: nativeUnit("kWh", "energy"),
    WattHours: nativeUnit("Wh", "energy"),
    MegawattHours: nativeUnit("MWh", "energy"),
    GigawattHours: nativeUnit("GWh", "energy"),
    Joules: nativeUnit("J", "energy"),
    Kilojoules: nativeUnit("kJ", "energy"),
    Megajoules: nativeUnit("MJ", "energy"),
    Gigajoules: nativeUnit("GJ", "energy"),
    // Btu/therm — not in `convert`; anchored to kWh. MBtu = MMBtu = Dth = 1e6 Btu.
    BritishThermalUnits: derivedUnit("kWh", KWH_PER_KBTU / 1000, "energy"),
    KBtus: derivedUnit("kWh", KWH_PER_KBTU, "energy"),
    MBtus: derivedUnit("kWh", KWH_PER_KBTU * 1000, "energy"),
    MMBtus: derivedUnit("kWh", KWH_PER_KBTU * 1000, "energy"),
    Therms: derivedUnit("kWh", KWH_PER_KBTU * 100, "energy"),
    Decatherms: derivedUnit("kWh", KWH_PER_KBTU * 1000, "energy"),
    // Chilled-water ton-hour ≈ 12,000 Btu.
    CHWTonHs: derivedUnit("kWh", 3.516998828, "energy"),
    // Area
    SquareMeters: nativeUnit("m2", "area"),
    SquareFeet: nativeUnit("ft2", "area"),
    Acres: nativeUnit("acre", "area"),
    Hectares: nativeUnit("ha", "area"),
    // Volume — native m3 / L / ft3 / gal / imp gal; utility multiples derived
    Litres: nativeUnit("L", "volume"),
    Hectolitres: nativeUnit("hL", "volume"),
    Kilolitres: nativeUnit("kL", "volume"),
    CubicMeters: nativeUnit("m3", "volume"),
    StandardCubicMeters: nativeUnit("m3", "volume"),
    KiloCubicMeters: derivedUnit("m3", 1000, "volume"),
    CubicFeet: nativeUnit("ft3", "volume"),
    DecaCubicFeet: derivedUnit("ft3", 10, "volume"),
    CentaCubicFeet: derivedUnit("ft3", 100, "volume"), // utility CCF = 100 ft³
    HectoCubicFeet: derivedUnit("ft3", 100, "volume"),
    KiloCubicFeet: derivedUnit("ft3", 1000, "volume"),
    MegaCubicFeet: derivedUnit("ft3", 1e6, "volume"),
    TeraCubicFeet: derivedUnit("ft3", 1e12, "volume"),
    CubicYards: nativeUnit("yd3", "volume"),
    Cords: derivedUnit("ft3", 128, "volume"),
    UsGallons: nativeUnit("gal", "volume"),
    DecaUsGallons: derivedUnit("gal", 10, "volume"),
    CentiUsGallons: derivedUnit("gal", 100, "volume"), // utility cGal = 100 gal
    HectoUsGallons: derivedUnit("gal", 100, "volume"),
    KiloUsGallons: derivedUnit("gal", 1000, "volume"),
    MegaUsGallons: derivedUnit("gal", 1e6, "volume"),
    TeraUsGallons: derivedUnit("gal", 1e12, "volume"),
    ImperialGallons: nativeUnit("imp gal", "volume"),
    CentiImperialGallons: derivedUnit("imp gal", 100, "volume"),
    KiloImperialGallons: derivedUnit("imp gal", 1000, "volume"),
    MegaImperialGallons: derivedUnit("imp gal", 1e6, "volume"),
    // Mass
    Kilograms: nativeUnit("kg", "mass"),
    Grams: nativeUnit("g", "mass"),
    Pounds: nativeUnit("lb", "mass"),
    Kilopounds: derivedUnit("lb", 1000, "mass"),
    Megapounds: derivedUnit("lb", 1e6, "mass"),
    Tonnes: nativeUnit("t", "mass"),
    USTons: nativeUnit("short ton", "mass"),
    ImperialTons: nativeUnit("long ton", "mass"),
    // Emissions — CO2e is dimensionally mass; anchor to kg, keep the label so it
    // never cross-converts into plain mass.
    KilogramCO2e: derivedUnit("kg", 1, "emissions"),
    TonneCO2e: derivedUnit("kg", 1000, "emissions"),
    // Temperature — native; `convert` applies the offset
    Celsius: nativeUnit("C", "temperature"),
    Fahrenheit: nativeUnit("F", "temperature"),
    Kelvin: nativeUnit("K", "temperature"),
    // Power
    Watts: nativeUnit("W", "power"),
    Kilowatts: nativeUnit("kW", "power"),
    Megawatts: nativeUnit("MW", "power"),
    Horsepower: nativeUnit("hp", "power"),
    // Length
    Meters: nativeUnit("m", "length"),
    Feet: nativeUnit("ft", "length"),
    Kilometers: nativeUnit("km", "length"),
    Inches: nativeUnit("in", "length"),
    Yards: nativeUnit("yd", "length"),
    // Time
    Days: nativeUnit("day", "time"),
    Hours: nativeUnit("hour", "time"),
    Weeks: nativeUnit("week", "time"),
    Months: nativeUnit("month", "time"),
    Years: nativeUnit("year", "time"),
};
// Merge the resource-typed fuel energy-content units (fuel.ts) as kWh-anchored
// energy units — the custom fuel energy units (NGCfs, DieselUSGs, …).
// A fuel quantity resolves to these via RESOURCE_UNIT_REMAP in convertCanonical.
for (const [name, kwh] of Object.entries(FUEL_ENERGY_KWH)) {
    UNIT_TABLE[name] = derivedUnit("kWh", kwh, "energy");
}
const LOWERCASE_UNIT_TABLE = Object.fromEntries(Object.entries(UNIT_TABLE).map(([key, ref]) => [key.toLowerCase(), ref]));
/**
 * Resolve a canonical unit name to a `convert`-anchored ref (exact, then
 * case-insensitive). Returns null when the name isn't in the table.
 */
export function resolveCanonicalUnit(name) {
    return UNIT_TABLE[name] ?? LOWERCASE_UNIT_TABLE[name.toLowerCase()] ?? null;
}
/** Dimension info for a canonical unit name, or null when unknown. */
export function canonicalUnitInfo(name) {
    if (!name)
        return null;
    const ref = resolveCanonicalUnit(name);
    return ref === null ? null : { canonical: name, dimension: ref.dimension };
}
/**
 * Convert a value between two canonical units of the same dimension, using the
 * shared `convert` library as the engine. Units `convert` doesn't ship
 * (Btu/therm, CO2e, fuel energy-content) are bridged by a linear factor onto a
 * native anchor.
 *
 * When `resourceType` is supplied, a fuel *quantity* is remapped to its energy
 * unit (heating value) — but only when the counterpart is energy, so a plain
 * volume→volume / mass→mass conversion is never routed through energy.
 *
 * Returns null when either unit is unknown or the dimensions differ; identity
 * when the units are equal.
 */
export function convertCanonical(value, fromUnit, toUnit, resourceType) {
    let from = fromUnit ?? null;
    let to = toUnit ?? null;
    if (from === to)
        return value; // equal -> identity
    if (from === null || to === null)
        return null;
    if (resourceType) {
        const fromDim = resolveCanonicalUnit(from)?.dimension ?? null;
        const toDim = resolveCanonicalUnit(to)?.dimension ?? null;
        from = remapFuelUnit(from, resourceType, toDim === "energy");
        to = remapFuelUnit(to, resourceType, fromDim === "energy");
    }
    const fromRef = resolveCanonicalUnit(from);
    const toRef = resolveCanonicalUnit(to);
    if (fromRef === null || toRef === null)
        return null;
    if (fromRef.dimension !== toRef.dimension)
        return null;
    // Express `value` in `from`'s native anchor, convert anchor->anchor via the
    // shared `convert` library, then express in `to`.
    const fromCode = fromRef.kind === "native" ? fromRef.code : fromRef.base;
    const toCode = toRef.kind === "native" ? toRef.code : toRef.base;
    const inFromAnchor = fromRef.kind === "derived" ? value * fromRef.factor : value;
    let inToAnchor;
    if (fromCode === toCode) {
        inToAnchor = inFromAnchor;
    }
    else if (canConvert(fromCode, toCode)) {
        inToAnchor = convertValue(inFromAnchor, fromCode, toCode);
    }
    else {
        return null;
    }
    return toRef.kind === "derived" ? inToAnchor / toRef.factor : inToAnchor;
}
//# sourceMappingURL=canonical.js.map